import { FileMinusIcon, FileTextIcon, ReceiptTextIcon, TruckIcon } from "lucide-react";
import type * as React from "react";
import { DOCUMENT_KINDS, type DocumentKind } from "@steelbill/types";
import { routes } from "@/routes/paths";

/**
 * How each KIND OF ISSUED DOCUMENT is shown and where its row goes.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THIS FILE EXISTS — it is `features/drafts/draft-kind.tsx` for documents,
 * and for the same reason.
 *
 * That file was written after a ternary (`kind === "INVOICE" ? … : …`) silently
 * rendered a CHALLAN draft as a QUOTATION and opened the wrong editor. Twenty
 * lines below the comment describing that bug, `RecentDocuments.tsx` had the
 * identical shape for ISSUED documents — `doc.kind === "invoice" ?
 * routes.invoice : routes.quotation` for the destination, and again for the
 * glyph — so the day the server started sending challan cards, tapping one
 * would have opened quotation number 7, or a 404 where no such quotation
 * existed. That is not a hypothetical: it is the exact failure one directory
 * over, in a file that names it.
 *
 * A ternary cannot be exhaustive. A `Record<DocumentKind, …>` cannot be
 * anything else, and `DOCUMENT_KINDS` in `@steelbill/types` is the single list
 * both this and the API's `mergeRecentDocuments` are keyed by. §17.3's credit
 * note DID fail the build here, on 2026-08-16, at the moment the kind was
 * invented rather than the morning the owner noticed a row was missing — which
 * is the entire return on this file.
 *
 * THE ICONS ARE THE SIDEBAR'S AND THE REGISTERS' — a bill is a receipt, a
 * quotation is a sheet of paper, a challan is a truck — and they match
 * `DRAFT_KIND_ICON` glyph for glyph, so an unfinished bill and an issued one
 * wear the same mark. COLOUR NEVER CARRIES THE KIND (the status tone does), so
 * these are deliberately unstyled beyond their size.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const DOCUMENT_KIND_ICON: Record<DocumentKind, React.ReactNode> = {
  invoice: <ReceiptTextIcon aria-hidden="true" className="size-4" />,
  quotation: <FileTextIcon aria-hidden="true" className="size-4" />,
  challan: <TruckIcon aria-hidden="true" className="size-4" />,
  /*
   * A SHEET WITH A MINUS ON IT — the document that REDUCES another (§34(1)).
   *
   * THE MINUS IS IN THE GLYPH AND NEVER IN A FIGURE, and the two are different
   * rules rather than one being bent. §17.3 forbids a negative AMOUNT anywhere:
   * every paise value on a credit note is stored and printed positive, because a
   * minus sign in the data is how a sum silently changes direction. What carries
   * the meaning "this reduces" is the document TYPE — and an icon is exactly
   * that, a mark for the type, on a row where the number beside it stays
   * positive.
   *
   * It is a FILE rather than a receipt or a banknote on purpose: a receipt is
   * the bill, and a banknote-with-a-down-arrow would read as money going out,
   * which is a refund. Nothing was paid back; the bill got smaller.
   */
  creditNote: <FileMinusIcon aria-hidden="true" className="size-4" />,
};

/**
 * The screen one document opens on, per kind.
 *
 * Every entry is a DETAIL route, never an editor: opening a row from a list is
 * reading, and the detail screen is the one that decides whether this document
 * may still be changed (an issued invoice never may — §17.3, CLAUDE.md).
 */
export const DOCUMENT_KIND_HREF: Record<DocumentKind, (id: number | string) => string> = {
  invoice: routes.invoice,
  quotation: routes.quotation,
  challan: routes.challan,
  /*
   * `routes.creditNote`, NOT `routes.creditNoteFor`. The second one takes an
   * INVOICE id and opens the editor; this map is keyed by the credit note's own
   * id and every entry in it is a DETAIL route. Handing a note's id to the
   * editor builder would compile perfectly and open a blank credit note against
   * whichever invoice happened to share that number.
   */
  creditNote: routes.creditNote,
};

/**
 * WHAT THE AMOUNT ON THIS DOCUMENT IS A FIGURE OF, and therefore how it may be
 * printed beside the others.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE ONE JUDGEMENT IN THIS FILE, AND THE REASON A MECHANICAL SWEEP WOULD HAVE
 * BEEN WORSE THAN THE GAP IT CLOSED.
 *
 * An invoice's amount is money the customer owes. A quotation's is money being
 * asked for. A CHALLAN'S IS NEITHER: Rule 55(1) requires a taxable value on a
 * delivery challan, so the figure is real and computed by the same engine, but
 * it is the DECLARED VALUE OF THE GOODS IN TRANSIT — nobody owes it, none of it
 * is output tax, and none of it reaches GSTR-3B. The app already says this in
 * those words in three places (`challan.declaredValue`,
 * `challan.notATaxInvoice`, and `DeclaredValue` in ChallanListScreen.tsx), and
 * a row on the home screen must agree with them.
 *
 * So a challan row prints its figure in MUTED type under a caption naming what
 * it is, rather than in the same weight as the bill above it. Same number, and
 * it is not dressed as a receivable. Dropping the figure entirely was the other
 * option and is worse: the owner recognises a delivery by what went on it.
 *
 * `null` means "no caption, this is money" — the invoice and quotation case,
 * where a caption would be noise on every row to explain the two rows that need
 * it. A new kind must state which it is, which is the whole point of the map.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export interface DocumentAmountStyle {
  /** An i18n key naming the figure, or `null` when it is plainly money owed. */
  captionKey: string | null;
  /** Tailwind classes for the figure itself. */
  className: string;
}

export const DOCUMENT_AMOUNT_STYLE: Record<DocumentKind, DocumentAmountStyle> = {
  invoice: { captionKey: null, className: "font-medium" },
  quotation: { captionKey: null, className: "font-medium" },
  challan: { captionKey: "challan.valueCaption", className: "text-muted-foreground" },
  /*
   * §17.3 — MONEY, AND IT MOVES THE OTHER WAY, so it takes a caption for the
   * opposite reason a challan does.
   *
   * A challan's figure is captioned because nobody owes it. A credit note's IS
   * owed-money arithmetic: it is real output tax being reversed, and the
   * customer owes exactly that much less. But it is the only amount in this app
   * that SUBTRACTS, and it is printed POSITIVE (§17.3 — no negative paise, ever;
   * the document type carries the direction). A positive rupee figure sitting in
   * a list beside four bills, in the same weight as them, therefore reads as a
   * fifth sale — the number is right and the meaning is inverted, which is worse
   * than a number that is merely wrong.
   *
   * So it keeps the full weight (it is money, unlike a challan's) and takes a
   * caption naming the direction. The caption is what makes the missing minus
   * sign safe.
   */
  creditNote: { captionKey: "creditNote.valueCaption", className: "font-medium" },
};

/**
 * Every document kind, in the order lists present them.
 *
 * Re-exported so a screen keyed by kind imports ONE name from here rather than
 * reaching past this file into `@steelbill/types` for the tuple and back here
 * for the maps — which is how one of them ends up iterating a list the maps do
 * not cover.
 */
export const DOCUMENT_KIND_ORDER: readonly DocumentKind[] = DOCUMENT_KINDS;
